import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Markdown } from 'tiptap-markdown';
import { useEffect } from 'react';
import EditorToolbar from './EditorToolbar';

interface Props {
  chapterId: string;
  initialMarkdown: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
}

export default function RichEditor({ chapterId, initialMarkdown, onChange, placeholder }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit už bundluje: Bold, Italic, Code, Heading, BulletList, OrderedList,
        // ListItem, Blockquote, CodeBlock, HorizontalRule, HardBreak, History.
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({
        HTMLAttributes: { class: 'my-6 rounded-lg border border-gray-200 shadow-sm' },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? 'Piš obsah kapitoly…',
      }),
      // Tabulky — bez nich tiptap-markdown nedokáže round-trip GFM tabulky a flatne je
      // při uložení na souvislý text. Resizable=false pro jednoduchost; kdyby bylo
      // potřeba mít drag handles, dá se zapnout.
      Table.configure({ resizable: false, HTMLAttributes: { class: 'doc-table' } }),
      TableRow,
      TableHeader,
      TableCell,
      Markdown.configure({
        html: false,
        tightLists: true,
        linkify: true,
        breaks: false,
      }),
    ],
    content: markdownToContent(initialMarkdown),
    onUpdate: ({ editor }) => {
      const md = getMarkdown(editor);
      onChange(md);
    },
    editorProps: {
      attributes: {
        class: 'prose-doc min-h-[60vh] max-w-none outline-none',
      },
    },
  });

  // Pokud chapterId změní (navigace mezi kapitolami), přenačti obsah.
  useEffect(() => {
    if (!editor) return;
    const current = getMarkdown(editor);
    if (current !== initialMarkdown) {
      editor.commands.setContent(markdownToContent(initialMarkdown), { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  return (
    <div>
      <EditorToolbar editor={editor} chapterId={chapterId} />
      <EditorContent editor={editor} />
    </div>
  );
}

// tiptap-markdown akceptuje buď markdown string nebo HTML — pro initial se chová jako markdown.
function markdownToContent(md: string): string {
  return md || '';
}

// Storage access — tiptap-markdown v0.9 nemá TS typy, proto přes unknown cast.
interface MarkdownStorage {
  getMarkdown?: () => string;
}
function getMarkdown(editor: { storage: unknown }): string {
  const storage = editor.storage as Record<string, MarkdownStorage | undefined>;
  return storage.markdown?.getMarkdown?.() ?? '';
}
